/**
 * A repeating group's ENTRIES, as the item form holds and sends them
 * (KPR-680; epic KPE-027). Pure: no React, no DOM, no network.
 *
 * What an item holds under a group (keepr-api docs/SCHEMA.md § Repeating group
 * values) is an ordered list. Each entry is `{ id, elements: { <column>: … } }`
 * or, on a group with `sections`, a heading `{ id, section: '<title>' }`. keepr
 * issues the id; a client never chooses one, and an entry takes no other key.
 * An item that holds none reads `''`.
 *
 * In the FORM an entry that keepr has not issued an id for yet (one just
 * added, or one a draft or a Duplicate brought) carries a client-only `_key`,
 * so React and the error map can name it. `_key` is stripped at the wire edge
 * (`entriesForWire`), and the id of a copied entry with it: a new item has none.
 *
 * This file owns:
 *   - the model (keys, headings, counts, limits, a new entry, its summary);
 *   - what a person may see and change (hidden and see-only columns);
 *   - what the form refuses before a save (`validateGroupEntries`);
 *   - the wire (`entriesForWire`);
 *   - the plan for an existing item: the list a person left is turned into the
 *     single-entry operations that produce it (`planEntryChanges`), ordered to
 *     stay inside the group's limits and to move as few entries as possible.
 */

import { getElementInitialValue, getElementValidationErrors, isElementValueEmpty, isServerAssignedElement } from './elements';
import { columnsOf, isRepeatingGroup, maxEntriesOf } from '../config/repeatingGroups';
import { accessOf, isHiddenFromMe, isSeeOnlyElement } from './elementAccess';

/** The longest a heading's title may be (the API's rule: 1 to 255 characters). */
export const HEADING_MAX_LENGTH = 255;

/** More single-entry operations than this go as the whole list instead. */
export const MAX_ENTRY_OPERATIONS = 20;

/** Above this many entries the form starts them folded to their summary line. */
export const FOLD_ABOVE = 25;

const isObject = value => !!value && typeof value === 'object' && !Array.isArray(value);

// ---- Keys -------------------------------------------------------------------

const generatedKeys = new WeakMap();
let keyCounter = 0;

/**
 * The stable name of an entry for React, the error map and the operation
 * plan: the form's `_key` once it has one, else the id keepr issued, else a
 * key made once for the object (an entry a draft carried back).
 */
export function entryKey(entry) {
	if (!isObject(entry)) return '';
	if (entry._key) return entry._key;
	if (entry.id) return entry.id;
	let key = generatedKeys.get(entry);
	if (!key) {
		keyCounter += 1;
		key = `new-${keyCounter}`;
		generatedKeys.set(entry, key);
	}
	return key;
}

/** A fresh client key, for an entry the form makes. */
export function makeEntryKey() {
	keyCounter += 1;
	return `new-${keyCounter}`;
}

/** The entry with its key written on it (the same object when it already has one). */
export const withKey = entry => (isObject(entry) && !entry._key ? { ...entry, _key: entryKey(entry) } : entry);

// ---- The model --------------------------------------------------------------

/** A heading entry (a title, no columns). */
export const isHeading = entry => isObject(entry) && typeof entry.section === 'string';

/** A group's value as a list of entries: `''`, `null` and anything else are none. */
export const entriesOf = value => (Array.isArray(value) ? value.filter(isObject) : []);

/** The key the error map files an entry's column under. */
export const entryErrorKey = (groupName, key, column) => `${groupName}.${key}.${column}`;
/** …a heading's title… */
export const HEADING_COLUMN = '__heading';
/** …and a sentence about the whole entry (it was removed since, say). */
export const ENTRY_COLUMN = '__entry';

/** Every error filed for one group: `{ '<key>.<column>': message }`. */
export function errorsForGroup(errors, groupName) {
	const prefix = `${groupName}.`;
	const out = {};
	Object.keys(errors || {}).forEach(name => {
		if (name.startsWith(prefix)) out[name.slice(prefix.length)] = errors[name];
	});
	return out;
}

/**
 * The columns this person is shown: those not hidden from them. An editor's
 * card carries a hidden column stamped `access: 'hidden'` (KPR-688); its
 * values never arrive, so it is not drawn and never sent.
 */
export const visibleColumns = group => columnsOf(group).filter(column => column?.name && !isHiddenFromMe(column));

/**
 * The columns this person may CHANGE. A see-only column is shown, disabled,
 * and never sent: a kept entry keeps its value, a new entry starts at its
 * default (SCHEMA.md § Column rules). A calculated column (KPR-686) is keepr's
 * to work out and is never sent either: the single-entry doors refuse a
 * value named for one (400 `driven_element`).
 */
export const editableColumns = group => visibleColumns(group)
	.filter(column => !isSeeOnlyElement(column) && !isServerAssignedElement(column));

/** The limits of a group: `{ min, max }` (the effective numbers). */
export function limitsOf(group) {
	const min = group?.options?.minEntries;
	return {
		min: Number.isInteger(min) && min > 0 ? min : 0,
		max: maxEntriesOf(group),
	};
}

/** How many entries a list holds: all of them (headings count toward the maximum) and the real ones (the minimum counts only those). */
export function countEntries(list) {
	const entries = entriesOf(list);
	const headings = entries.filter(isHeading).length;
	return { total: entries.length, headings, real: entries.length - headings };
}

/**
 * How many entries a group's value holds, in words, for a surface that can
 * only print text (a tile, a preview window, the Source records list): "3
 * entries", "1 entry", and `''` when there are none. Section headings are
 * not entries, and a value that is not a list (`''`, null, an object) holds
 * none. It prints no column value, so it shows nothing a reader's column
 * access would hide.
 */
export function entryCountWords(value) {
	return entryNumberWords(countEntries(value).real);
}

/** "3 entries", "1 entry", `''` for none: a number of entries in words, for a count that is not a list (the Source records modal, KPR-1121). */
export function entryNumberWords(count) {
	return count ? `${count} ${count === 1 ? 'entry' : 'entries'}` : '';
}

/** Sections on? A heading already stored stays whether or not the switch is. */
export const allowsHeadings = group => group?.options?.sections === true;

/** May this group be changed by this person at all? (A see-only group is read-only.) */
export const isGroupWritable = group => !isSeeOnlyElement(group);

// ---- Words ------------------------------------------------------------------

// "Ingredient" reads as "ingredient" mid-sentence; "USB port" and "SKU" keep
// their capitals. Lowered only when the word is a plain capitalised one.
export function lowerLabel(text) {
	const value = String(text || '').trim();
	if (!value) return '';
	return /^[A-Z][a-z]/.test(value) ? value.charAt(0).toLowerCase() + value.slice(1) : value;
}

/** The group's singular and plural, for a sentence ("ingredient", "ingredients"). */
export function nounsOf(group) {
	const singular = String(group?.label?.singular || group?.name || 'entry').trim() || 'entry';
	const plural = String(group?.label?.plural || '').trim() || (singular.endsWith('s') ? singular : `${singular}s`);
	return { singular: lowerLabel(singular), plural: lowerLabel(plural), singularLabel: singular, pluralLabel: plural };
}

/** "1 ingredient", "3 ingredients". */
export const countPhrase = (group, n) => {
	const { singular, plural } = nounsOf(group);
	return `${n} ${n === 1 ? singular : plural}`;
};

/** The label on the Add button: "Add ingredient". */
export const addLabel = group => `Add ${nounsOf(group).singular}`;

/** What the limits come to, in a sentence: "At least 2, up to 100." */
export function limitsSentence(group) {
	const { min, max } = limitsOf(group);
	return min > 0 ? `At least ${min}, up to ${max}.` : `Up to ${max}.`;
}

/** Why Add is off at the maximum. */
export function atMaxSentence(group) {
	const { max } = limitsOf(group);
	return `This group holds up to ${max} ${max === 1 ? 'entry' : 'entries'}. Remove one to add another.`;
}

/** The sentence under the group while it holds fewer than its minimum. */
export function belowMinSentence(group, real) {
	const { min } = limitsOf(group);
	const missing = min - real;
	return `Add ${missing} more to reach the minimum of ${min}.`;
}

/** What an entry is called by someone who cannot see it: "ingredient 3", "heading Topping". */
export function entryName(group, list, entry) {
	const { singular } = nounsOf(group);
	if (isHeading(entry)) return entry.section ? `heading ${entry.section}` : 'heading';
	const index = entriesOf(list).filter(item => !isHeading(item)).indexOf(entry);
	return index >= 0 ? `${singular} ${index + 1}` : singular;
}

// ---- A new entry ------------------------------------------------------------

/**
 * A new entry at its columns' defaults (a default value, today, now; read in
 * the collection's zone), as a new item starts. A see-only column is seeded
 * too, so it reads as it will be, and is not sent.
 */
export function newEntry(group, { zone } = {}) {
	const elements = {};
	visibleColumns(group).forEach(column => {
		elements[column.name] = getElementInitialValue(column, { isNewRecord: true, zone });
	});
	return { _key: makeEntryKey(), elements };
}

/** A new heading, untitled. */
export const newHeading = () => ({ _key: makeEntryKey(), section: '' });

// ---- Reading an entry -------------------------------------------------------

/**
 * One short line for an entry — what folds into the closed row and the
 * reorder row: its first few filled columns, read the way every other
 * surface reads them. `format(column, value)` is the caller's formatter
 * (itemValueDisplay.formatValue with the lookup resolver).
 */
export function entrySummary(group, entry, format, { max = 3 } = {}) {
	if (isHeading(entry)) return entry.section || '';
	const parts = [];
	for (const column of visibleColumns(group)) {
		const value = entry?.elements?.[column.name];
		if (isElementValueEmpty(column, value)) continue;
		if (column.dataType === 'file') continue;
		const text = String(format(column, value) ?? '').trim();
		if (text) parts.push(text);
		if (parts.length >= max) break;
	}
	return parts.join(' · ');
}

// ---- A table's cell ---------------------------------------------------------

/** The most entries a table cell may join (the API's rule for a layout's `display.first`). */
export const GROUP_DISPLAY_FIRST_MAX = 10;
/** How many entries a cell joins when the layout says none. */
export const GROUP_DISPLAY_FIRST_DEFAULT = 3;

/**
 * A table layout column's `display` for a repeating group (KPR-683; keepr-api
 * docs/SCHEMA.md "Card layouts"), normalized against the group this person
 * sees: `{ as: 'entries', column, first }` when it names a column they may
 * read, else null — the count, the default. A stale layout (the element was
 * retyped, the column removed or hidden from them) reads as the count rather
 * than as an error.
 */
export function groupDisplayOf(group, display) {
	if (!isRepeatingGroup(group) || !isObject(display) || display.as !== 'entries') return null;
	const column = visibleColumns(group).find(candidate => candidate.name === display.column);
	if (!column) return null;
	const first = Number.isInteger(display.first) && display.first >= 1 && display.first <= GROUP_DISPLAY_FIRST_MAX
		? display.first
		: GROUP_DISPLAY_FIRST_DEFAULT;
	return { as: 'entries', column: column.name, first };
}

/**
 * What a table cell shows for a group: `{ count, text, more, column }` — the
 * number of entries (headings are none), and, when the layout chose a column,
 * that column of the first `first` entries joined (`text`, '' when they hold
 * nothing there), how many entries come after them (`more`) and the column's
 * definition (`column`, so the caller can tell a MASKED one, KPR-1353). `text`
 * is null (and `column` null) for the count. `format(column, value)` is the
 * caller's one reading of a value (itemValueDisplay.formatValue with its
 * lookup resolver — masked by default). Only columns this person may read are
 * ever read.
 */
export function groupCellSummary(group, value, display, format) {
	const entries = entriesOf(value).filter(entry => !isHeading(entry));
	const count = entries.length;
	const chosen = groupDisplayOf(group, display);
	if (!chosen) return { count, text: null, more: 0, column: null };
	const column = visibleColumns(group).find(candidate => candidate.name === chosen.column);
	const shown = entries.slice(0, chosen.first);
	const parts = shown
		.map((entry) => {
			const raw = entry?.elements?.[column.name];
			return isElementValueEmpty(column, raw) ? '' : String(format(column, raw) ?? '').trim();
		})
		.filter(Boolean);
	return { count, text: parts.join(', '), more: count - shown.length, column };
}

// ---- Values: same or different ----------------------------------------------

// JSON with object keys sorted: a measurement the API hands back with its keys
// in another order is the same value. Every empty spelling is one value.
const stable = value => JSON.stringify(value, (key, v) => (v && typeof v === 'object' && !Array.isArray(v)
	? Object.keys(v).sort().reduce((acc, k) => { acc[k] = v[k]; return acc; }, {})
	: v));
const isBlank = v => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
export function sameColumnValue(a, b) {
	if (isBlank(a) && isBlank(b)) return true;
	try { return stable(a) === stable(b); } catch { return false; }
}

// ---- Validation -------------------------------------------------------------

/**
 * What the form refuses about a group before it is sent, as the error map
 * the form keeps (`{ [name]: message }`):
 *
 *   `<group>`                     the group's own sentence (the minimum, or
 *                                 "n entries need attention" when any entry
 *                                 below does), the key the form scrolls to
 *   `<group>.<entryKey>.<column>` a column's message, beside the column
 *   `<group>.<entryKey>.__heading` a heading's
 *
 * `stored` is the list the item holds (the echo rule: a value an entry
 * already has is never refused here, as the API never refuses it).
 */
export function validateGroupEntries(group, list, { stored = null, attachmentsOff = false } = {}) {
	const errors = {};
	const name = group?.name;
	if (!name || !isGroupWritable(group)) return errors;
	const entries = entriesOf(list);
	const storedByKey = new Map(entriesOf(stored).map(entry => [entry.id, entry]));
	const columns = visibleColumns(group);
	let failing = 0;
	entries.forEach(entry => {
		const key = entryKey(entry);
		if (isHeading(entry)) {
			const title = entry.section.trim();
			if (!title) {
				errors[entryErrorKey(name, key, HEADING_COLUMN)] = 'Give this heading a title.';
				failing += 1;
			} else if (title.length > HEADING_MAX_LENGTH) {
				errors[entryErrorKey(name, key, HEADING_COLUMN)] = `A heading holds up to ${HEADING_MAX_LENGTH} characters.`;
				failing += 1;
			}
			return;
		}
		const storedEntry = entry.id ? storedByKey.get(entry.id) : null;
		const found = getElementValidationErrors(columns, entry.elements || {}, {
			stored: storedEntry?.elements || null,
			attachmentsOff,
		});
		// `required` is not asked again of what a kept entry already holds blank
		// (SCHEMA.md: a kept entry's unchanged columns are excepted), so an author
		// who made a column required later does not make a person fill in 30
		// entries they never touched.
		if (storedEntry) {
			Object.keys(found).forEach(column => {
				const def = columns.find(col => col.name === column);
				if (def && isElementValueEmpty(def, entry.elements?.[column]) && isElementValueEmpty(def, storedEntry.elements?.[column])) delete found[column];
			});
		}
		const columnNames = Object.keys(found);
		if (columnNames.length) failing += 1;
		columnNames.forEach(column => { errors[entryErrorKey(name, key, column)] = found[column]; });
	});
	const { min } = limitsOf(group);
	const real = countEntries(entries).real;
	if (real < min) {
		errors[name] = `Add at least ${countPhrase(group, min)}.`;
	} else if (failing) {
		errors[name] = failing === 1 ? '1 entry needs attention.' : `${failing} entries need attention.`;
	}
	return errors;
}

/** The first entry key that has an error, and its first column, in list order. */
export function firstEntryError(group, list, errors) {
	const mine = errorsForGroup(errors, group?.name);
	for (const entry of entriesOf(list)) {
		const key = entryKey(entry);
		const prefix = `${key}.`;
		const hit = Object.keys(mine).find(name => name.startsWith(prefix));
		if (hit) return { key, column: hit.slice(prefix.length) };
	}
	return null;
}

/**
 * The error map after a group's list changed: an error stays while its cell
 * is still wrong, so a message a save attempt raised clears the moment the cell
 * is put right — and nothing new appears before the next attempt. The same
 * map back when nothing of the group was in it.
 *
 * What the live check cannot judge is a sentence the SERVER gave (a lookup
 * that is gone, a value that must be unique): it stays until the cell it names
 * is changed (`previous`, the list as it stood a moment ago, says which cell
 * was) or its entry goes — an edit to some other entry never drops it. Without
 * `previous`, an error goes as soon as the live check is satisfied.
 */
export function reconcileGroupErrors(errors, group, list, { stored = null, attachmentsOff = false, previous = null } = {}) {
	const name = group?.name;
	const prefix = `${name}.`;
	const had = Object.keys(errors || {}).filter(key => key === name || key.startsWith(prefix));
	if (!had.length) return errors;
	const fresh = validateGroupEntries(group, list, { stored, attachmentsOff });
	const now = entriesOf(list);
	const before = previous ? new Map(entriesOf(previous).map(entry => [entryKey(entry), entry])) : null;
	const next = { ...errors };
	let changed = false;
	const cellChanged = (entry, entryPart, column) => {
		if (!before) return true;
		const was = before.get(entryPart);
		if (!was) return true;
		if (column === HEADING_COLUMN) return entry.section !== was.section;
		if (column === ENTRY_COLUMN) return !sameColumnValue(entry.elements, was.elements);
		return !sameColumnValue(entry.elements?.[column], was.elements?.[column]);
	};
	let remaining = 0;
	had.forEach(key => {
		if (key === name) return;
		const rest = key.slice(prefix.length);
		const dot = rest.indexOf('.');
		const entryPart = dot < 0 ? rest : rest.slice(0, dot);
		const column = dot < 0 ? '' : rest.slice(dot + 1);
		const entry = now.find(item => entryKey(item) === entryPart);
		if (!entry || (fresh[key] === undefined && cellChanged(entry, entryPart, column))) {
			delete next[key];
			changed = true;
		} else {
			remaining += 1;
		}
	});
	// The group's own sentence: gone when nothing under it still needs saying.
	if (had.includes(name) && fresh[name] === undefined && remaining === 0) {
		delete next[name];
		changed = true;
	}
	return changed ? next : errors;
}

// ---- The wire ---------------------------------------------------------------

// The empty spelling of a column an entry has no value for: '' for text, null
// for a date or a measurement, [] for a list (the element's own).
const emptyOf = column => getElementInitialValue(column, { isNewRecord: false });

/** An entry's changeable columns as they go on the wire. */
function wireColumns(columns, source) {
	const elements = {};
	columns.forEach(column => {
		const held = source?.[column.name];
		elements[column.name] = held === undefined ? emptyOf(column) : held;
	});
	return elements;
}

/**
 * The group's list as the API takes it. A new item sends no id (it has none to
 * claim); an existing item keeps each id it read, so the entry stays the same
 * entry. A see-only column is left out (a kept entry keeps its value), a hidden
 * one is never in the list. `_key` never goes.
 */
export function entriesForWire(group, list, { isNew = false } = {}) {
	const entries = entriesOf(list);
	if (!entries.length) return '';
	const columns = editableColumns(group);
	return entries.map(entry => {
		const out = {};
		if (!isNew && entry.id) out.id = entry.id;
		if (isHeading(entry)) {
			out.section = entry.section.trim();
		} else {
			out.elements = wireColumns(columns, entry.elements);
		}
		return out;
	});
}

/** Strip the form's own keys from every group list of an element map (a draft, a copy). */
export function withoutEntryKeys(groups, elements) {
	if (!isObject(elements)) return elements;
	const out = { ...elements };
	(groups || []).filter(isRepeatingGroup).forEach(group => {
		const list = out[group.name];
		if (!Array.isArray(list)) return;
		out[group.name] = list.map(entry => {
			if (!isObject(entry)) return entry;
			const { _key: _ignored, ...rest } = entry;
			return rest;
		});
	});
	return out;
}

// ---- The plan: a list a person left → single-entry operations ---------------

/** Longest increasing subsequence of `seq` (numbers), as a Set of its INDICES. */
export function longestIncreasingIndices(seq) {
	const n = seq.length;
	if (!n) return new Set();
	const tails = []; // indices into seq, the smallest tail value of each length
	const prev = new Array(n).fill(-1);
	for (let i = 0; i < n; i += 1) {
		let lo = 0;
		let hi = tails.length;
		while (lo < hi) {
			const mid = (lo + hi) >> 1;
			if (seq[tails[mid]] < seq[i]) lo = mid + 1; else hi = mid;
		}
		if (lo > 0) prev[i] = tails[lo - 1];
		tails[lo] = i;
	}
	const keep = new Set();
	for (let i = tails[tails.length - 1]; i !== undefined && i >= 0; i = prev[i]) keep.add(i);
	return keep;
}

/**
 * The moves that turn the order `from` into the order `to` (both lists of the
 * same keys), as `[{ key, position }]` — `position` the 1-based place the
 * entry holds after its move, which is what the API's move door takes. The
 * entries of a longest increasing run stay; every other entry moves once, to
 * just after the entry that precedes it in `to`.
 */
export function planMoves(from, to) {
	const sim = [...from];
	const index = new Map(to.map((key, i) => [key, i]));
	const keep = longestIncreasingIndices(sim.map(key => index.get(key)));
	const stays = new Set([...keep].map(i => sim[i]));
	const moves = [];
	to.forEach((key, i) => {
		if (stays.has(key)) return;
		sim.splice(sim.indexOf(key), 1);
		const after = i === 0 ? -1 : sim.indexOf(to[i - 1]);
		sim.splice(after + 1, 0, key);
		moves.push({ key, position: after + 2, afterKey: i === 0 ? null : to[i - 1] });
	});
	return moves;
}

/**
 * What the form has to do to turn the list the item HOLDS (`baseline`, as read)
 * into the list the person left (`current`), as single-entry operations in
 * the order to send them:
 *
 *   { op: 'update', key, entryId, elements } | { op: 'update', key, entryId, section }
 *   { op: 'add',    key, elements } | { op: 'add', key, section }, with `position`
 *   { op: 'remove', key, entryId }
 *   { op: 'move',   key, entryId, position }
 *
 * Updates carry only the columns that changed. Adds and removes are
 * interleaved so the list never leaves its limits on the way (removes first at
 * the maximum, adds first at the minimum); then the fewest moves put the rest
 * in order. `count` is the number of operations; `fits` is false when that is
 * more than `MAX_ENTRY_OPERATIONS`, and the caller sends the whole list.
 */
export function planEntryChanges(group, baseline, current) {
	const base = entriesOf(baseline);
	const now = entriesOf(current);
	const baseById = new Map(base.map(entry => [entry.id, entry]));
	const columns = editableColumns(group);
	const { min, max } = limitsOf(group);

	const kept = now.filter(entry => entry.id && baseById.has(entry.id));
	const keptIds = new Set(kept.map(entry => entry.id));
	const added = now.filter(entry => !(entry.id && baseById.has(entry.id)));
	const removed = base.filter(entry => !keptIds.has(entry.id));

	const ops = [];

	// Changed columns of the entries that stay.
	kept.forEach(entry => {
		const before = baseById.get(entry.id);
		if (isHeading(entry)) {
			if (isHeading(before) && entry.section.trim() !== before.section) {
				ops.push({ op: 'update', key: entryKey(entry), entryId: entry.id, section: entry.section.trim() });
			}
			return;
		}
		const changed = {};
		columns.forEach(column => {
			const held = entry.elements?.[column.name];
			if (!sameColumnValue(held, before?.elements?.[column.name])) {
				changed[column.name] = held === undefined ? emptyOf(column) : held;
			}
		});
		if (Object.keys(changed).length) ops.push({ op: 'update', key: entryKey(entry), entryId: entry.id, elements: changed });
	});

	// Adds and removes, never leaving the limits on the way.
	const order = base.map(entry => ({ key: entryKey(entry), id: entry.id, heading: isHeading(entry) }));
	const desired = now.map(entryKey);
	const toRemove = [...removed];
	const toAdd = [...added];
	let real = order.filter(item => !item.heading).length;
	const desiredSet = new Set(desired);
	while (toRemove.length || toAdd.length) {
		const next = toRemove[0];
		const canRemove = !!next && (isHeading(next) || real - 1 >= min);
		const canAdd = toAdd.length > 0 && order.length + 1 <= max;
		// A remove first when the limits allow it (the list stays small and the
		// places stay put); an add first when the minimum would be broken.
		let doRemove;
		if (canRemove) doRemove = true;
		else if (canAdd) doRemove = false;
		else doRemove = toRemove.length > 0;
		if (doRemove) {
			const entry = toRemove.shift();
			const at = order.findIndex(item => item.id === entry.id);
			if (at >= 0) order.splice(at, 1);
			if (!isHeading(entry)) real -= 1;
			ops.push({ op: 'remove', key: entryKey(entry), entryId: entry.id });
		} else {
			const entry = toAdd.shift();
			const key = entryKey(entry);
			// Just after the entry that precedes it in the list the person left.
			const wantedAt = desired.indexOf(key);
			let after = -1;
			for (let i = wantedAt - 1; i >= 0 && after < 0; i -= 1) after = order.findIndex(item => item.key === desired[i]);
			const position = after + 2;
			const afterKey = after >= 0 ? order[after].key : null;
			order.splice(after + 1, 0, { key, id: null, heading: isHeading(entry) });
			if (!isHeading(entry)) real += 1;
			const op = { op: 'add', key, position, afterKey };
			if (isHeading(entry)) op.section = entry.section.trim();
			else op.elements = wireColumns(columns, entry.elements);
			ops.push(op);
		}
	}

	// The order of what stays (and what was added) → the order left.
	const finalKeys = order.map(item => item.key).filter(key => desiredSet.has(key));
	planMoves(finalKeys, desired).forEach(move => {
		const entry = now.find(item => entryKey(item) === move.key);
		ops.push({ op: 'move', key: move.key, entryId: entry?.id || null, position: move.position, afterKey: move.afterKey });
	});

	return { ops, count: ops.length, fits: ops.length <= MAX_ENTRY_OPERATIONS };
}

/**
 * The place (1-based) an entry takes when it goes just after the entry
 * `afterId` (`null`: first) in `list` — the list as the SERVER holds it, so a
 * colleague's entries added or removed since this form read the item move the
 * place with them instead of throwing it off. `movingId` is an entry being
 * moved, which does not count while it is lifted. `null` when `afterId` is not
 * in the list (the caller keeps the place it planned).
 */
export function positionAfter(list, afterId, movingId = null) {
	const ids = entriesOf(list).map(entry => entry.id).filter(id => id && id !== movingId);
	if (afterId === null || afterId === undefined) return 1;
	const at = ids.indexOf(afterId);
	return at < 0 ? null : at + 2;
}

/**
 * The baseline list after one operation landed, so the next plan (a Save
 * again after a refusal) sends only what is left. `result` is the door's
 * answer (`{ entryId, position }`).
 */
export function applyOperation(list, op, result = {}) {
	const entries = entriesOf(list).map(entry => ({ ...entry }));
	if (op.op === 'update') {
		const at = entries.findIndex(entry => entry.id === op.entryId);
		if (at < 0) return entries;
		entries[at] = isHeading(entries[at])
			? { ...entries[at], section: op.section ?? entries[at].section }
			: { ...entries[at], elements: { ...(entries[at].elements || {}), ...(op.elements || {}) } };
	} else if (op.op === 'remove') {
		return entries.filter(entry => entry.id !== op.entryId);
	} else if (op.op === 'add') {
		const fresh = { id: result.entryId };
		if (op.section !== undefined) fresh.section = op.section; else fresh.elements = { ...(op.elements || {}) };
		const position = Number.isInteger(result.position) ? result.position : (op.position || entries.length + 1);
		entries.splice(Math.max(0, Math.min(entries.length, position - 1)), 0, fresh);
	} else if (op.op === 'move') {
		const at = entries.findIndex(entry => entry.id === op.entryId);
		if (at < 0) return entries;
		const [moved] = entries.splice(at, 1);
		entries.splice(Math.max(0, Math.min(entries.length, op.position - 1)), 0, moved);
	}
	return entries;
}

// ---- Which groups go how, on an existing item -------------------------------

/**
 * For each repeating group the person may change, how an existing item's save
 * writes it: `none` (nothing changed — left out of the write, so a colleague's
 * concurrent change to the group is never put back), `ops` (single-entry
 * operations, in `plan.ops`) or `whole` (the list goes in the PUT: more
 * changes than `MAX_ENTRY_OPERATIONS`). A group held read-only is left out.
 */
export function planGroupWrites(groups, current, baseline) {
	const out = {};
	(groups || []).filter(isRepeatingGroup).forEach(group => {
		if (!group?.name || !isGroupWritable(group)) { out[group?.name] = { mode: 'none', plan: null }; return; }
		const plan = planEntryChanges(group, baseline?.[group.name], current?.[group.name]);
		out[group.name] = { mode: plan.count === 0 ? 'none' : (plan.fits ? 'ops' : 'whole'), plan };
	});
	return out;
}

// ---- A refusal, placed on the entry it is about -------------------------------

/**
 * "elements.ingredient: entry 2 (k3m9x2qa7b), column "amount": Amount must be…"
 * → "Amount must be…". The API's messages start with where they are; the
 * sentence is shown where they are already.
 */
export function plainRefusal(text) {
	return String(text || '')
		.replace(/^elements\.[^:]+:\s*/, '')
		.replace(/^entry \d+(?: \([^)]*\))?,?\s*/, '')
		.replace(/^column "[^"]*":\s*/, '')
		.trim();
}

/**
 * Which entry of `list` a refusal names: by `entryId` when the API gave one,
 * else by `position` (1-based, headings counted) — the order the list was sent in.
 */
export function entryKeyFromRefusal(list, data) {
	const entries = entriesOf(list);
	if (data?.entryId) {
		const hit = entries.find(entry => entry.id === data.entryId);
		if (hit) return entryKey(hit);
	}
	if (Number.isInteger(data?.position) && data.position >= 1 && data.position <= entries.length) {
		return entryKey(entries[data.position - 1]);
	}
	return null;
}

// ---- Access, as the form reads it -------------------------------------------

/** The labels of the columns shown but not changeable (see-only). */
export const seeOnlyColumnNames = group => visibleColumns(group)
	.filter(column => accessOf(column) === 'see')
	.map(column => column.label?.singular || column.name);
